import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

export interface Ad {
  id: string;
  workspace_id: string;
  ad_set_id: string;
  campaign_id: string;
  ad_account_id: string;
  fb_ad_id: string | null;
  name: string;
  status: string;
  effective_status: string | null;
  status_stale_since: string | null;
  // Creative fields
  page_id: string | null;
  headline: string | null;
  body: string | null;
  headlines: string[] | null;
  bodies: string[] | null;
  call_to_action: string | null;
  link_url: string | null;
  image_path: string | null;
  fb_image_hash: string | null;
  video_path: string | null;
  fb_video_id: string | null;
  creative_image_url: string | null;
  // Legacy fields
  creative_id: string | null;
  fb_creative_id: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  campaign?: {
    id: string;
    name: string;
  };
  ad_set?: {
    id: string;
    name: string;
  };
  ad_account?: {
    id: string;
    name: string;
  };
  page?: {
    id: string;
    name: string;
    fb_page_id: string;
  };
}

export interface AdFilters {
  ad_set_id?: string;
  campaign_id?: string;
  ad_account_id?: string;
  status?: string | string[];
  search?: string;
  offset?: number;
  limit?: number;
}

export interface QueryResult<T> {
  data: T[];
  total: number;
}

function buildWhereCondition(filters: AdFilters): WhereCondition | undefined {
  const conditions: WhereCondition[] = [];

  if (filters.ad_set_id) {
    conditions.push({ ad: { ad_set_id: filters.ad_set_id } });
  }

  if (filters.campaign_id) {
    conditions.push({ ad: { campaign_id: filters.campaign_id } });
  }

  if (filters.ad_account_id) {
    conditions.push({ ad: { ad_account_id: filters.ad_account_id } });
  }

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    conditions.push({ ad: { status: { op: 'in', value: statuses } } });
  }

  if (filters.search) {
    conditions.push({ ad: { name: { op: 'ilike', value: `%${filters.search}%` } } });
  }

  return conditions.length > 0 ? { $and: conditions } : undefined;
}

export const adsService = {
  query: async (filters: AdFilters = {}): Promise<QueryResult<Ad>> => {
    const payload: QueryPayload = {
      where: buildWhereCondition(filters),
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 25,
      order_by: 'ad.created_at',
      order: 'desc',
    };

    const response = await api.post<QueryResponse<Ad>>('/ads/query', payload);
    return {
      data: response.data.data,
      total: response.data._meta?.total ?? 0,
    };
  },

  getById: async (id: string): Promise<Ad> => {
    const response = await api.get<ApiResponse<Ad>>(`/ads/${id}`);
    return response.data.data!;
  },

  create: async (data: Partial<Ad>): Promise<Ad> => {
    const response = await api.post<ApiResponse<Ad>>('/ads', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<Ad>): Promise<Ad> => {
    const response = await api.patch<ApiResponse<Ad>>(`/ads/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/ads/${id}`);
  },

  duplicate: async (id: string, data?: { name?: string }): Promise<Ad> => {
    const response = await api.post<ApiResponse<Ad>>(`/ads/${id}/duplicate`, data);
    return response.data.data!;
  },

  /** Get a signed URL for direct-to-GCS upload (for large files like videos) */
  getUploadUrl: async (filename: string, contentType: string, adSetId: string): Promise<{ uploadUrl: string; path: string }> => {
    const response = await api.post<ApiResponse<{ uploadUrl: string; path: string }>>('/ads/upload-url', {
      filename,
      contentType,
      ad_set_id: adSetId,
    });
    return response.data.data!;
  },

  uploadImage: async (file: File, adSetId: string): Promise<{ path: string }> => {
    const formData = new FormData();
    formData.append('ad_set_id', adSetId);
    formData.append('image', file);
    const response = await api.post<ApiResponse<{ path: string }>>('/ads/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data!;
  },

  /** Fetch fresh media URLs from Facebook for an ad's creative. */
  getMedia: async (adId: string): Promise<{
    thumbnail_url: string | null;
    image_url: string | null;
    video_id: string | null;
    video_source: string | null;
    video_thumbnail_urls: string[];
  }> => {
    const response = await api.get<ApiResponse<{
      thumbnail_url: string | null;
      image_url: string | null;
      video_id: string | null;
      video_source: string | null;
      video_thumbnail_urls: string[];
    }>>(`/ads/${adId}/media`);
    return response.data.data!;
  },
};
